import { useParams, Link } from "react-router-dom";
import { CalendarDays, Clock3, ArrowLeft, ArrowRight, User } from "lucide-react";
import SEO, { breadcrumbSchema } from "../components/SEO";
import CtaBand from "../components/CtaBand";
import BookingWidget from "../components/BookingWidget";
import Reveal from "../components/Reveal";
import { BLOG_POSTS } from "../data/blog";
import { SITE, img } from "../data/site";
import { useLang } from "../i18n";
import NotFound from "./NotFound";

export default function BlogPost() {
  const { slug } = useParams();
  const { t } = useLang();
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  if (!post) return <NotFound />;

  const schemas = [
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Blog", path: "/blog" },
      { name: post.title, path: `/blog/${post.slug}` },
    ]),
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.metaDesc,
      image: post.img,
      datePublished: post.date,
      author: { "@id": `${SITE.url}/#org` },
      publisher: { "@id": `${SITE.url}/#org` },
    },
  ];

  const related = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <>
      <SEO title={post.metaTitle.replace(" | Blue City Taxi Blog", "")} description={post.metaDesc} path={`/blog/${post.slug}`} image={post.img} schemas={schemas} />
      <article className="bg-white">
        <header className="relative bg-navy overflow-hidden grain">
          <img src={img(post.img, 1600)} alt={post.title} className="absolute inset-0 w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/70 to-navy/40" />
          <div className="relative max-w-3xl mx-auto px-6 pt-14 pb-16">
            <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center gap-1.5 text-xs font-bold text-white/70 hover:text-gold transition-colors">
              <ArrowLeft size={13} /> {t("backBlog")}
            </Link>
            <span className="block mt-5 text-[10px] font-extrabold uppercase tracking-[0.25em] text-gold">{post.tag}</span>
            <h1 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">{post.title}</h1>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-white/60">
              <span className="inline-flex items-center gap-1.5"><User size={13} /> Blue City Taxi</span>
              <span className="inline-flex items-center gap-1.5"><CalendarDays size={13} /> {new Date(post.date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</span>
              <span className="inline-flex items-center gap-1.5"><Clock3 size={13} /> {post.readTime}</span>
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-6 py-14 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 max-w-3xl">
            <p className="text-base sm:text-lg text-slate-700 leading-relaxed font-medium">{post.excerpt}</p>
            {post.content.map((s, i) => (
              <Reveal key={i} className="mt-9">
                <h2 className="font-heading text-2xl font-extrabold text-navy">{s.h}</h2>
                <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed">{s.p}</p>
              </Reveal>
            ))}
          </div>
          <aside className="lg:sticky lg:top-28 h-fit space-y-5">
            <BookingWidget compact />
            <div className="rounded-2xl border border-slate-200/80 bg-white p-5">
              <p className="text-xs font-bold uppercase tracking-widest text-gold-dark">{t("relatedBlog")}</p>
              <div className="mt-3 space-y-3">
                {related.map((r) => (
                  <Link key={r.slug} to={`/blog/${r.slug}`} data-testid={`related-${r.slug}`} className="group flex gap-3 items-center">
                    <img src={img(r.img, 200)} alt="" loading="lazy" className="w-16 h-16 rounded-xl object-cover shrink-0" />
                    <span className="text-[13px] font-semibold text-navy leading-snug group-hover:text-royal transition-colors">{r.title}</span>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </article>
      <CtaBand title={t("blogCtaTitle")} text={t("blogCtaText")} />
    </>
  );
}
